import { useTranslation } from "react-i18next";
import OAuthModal from "../modals/OAuthModal";
import { useRoomOAuthMembership } from "../../features/oauth/useRoomOAuthMembership";
import { useApiQuery } from "../../services/api";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import { useIsRoomWelcomeOpen } from "./RoomWelcomeHost";

/**
 * The partner sign-in gate of an OAuth room (SFCTCV, docs/partner-oauth-rooms.md):
 * on every entry into the room it revalidates the reader's partner membership,
 * which also rolls the background token renewal forward, and puts the sign-in
 * or renewal modal over the room while the membership does not hold. Mounted by
 * the room's landing on mobile and by the room frame on desktop. Renders
 * nothing in any other room. The modal waits for the room welcome sheet
 * (docs/partner-objection.md, REQ-14), as the other room prompts do: a member
 * who never saw the sheet and whose partner session lapsed gets the sheet,
 * then the modal, never both stacked.
 */
export function RoomOAuthGate({
  roomId,
}: {
  roomId: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { roomTextLang } = useActiveLang();
  const roomQuery = useApiQuery("/webapp/private/rooms/:id", {
    param: { id: roomId },
  });
  const membership = useRoomOAuthMembership(roomId);
  const isRoomWelcomeOpen = useIsRoomWelcomeOpen();

  if (membership.kind === "none" || roomQuery.state !== "success") return null;
  const { errorCode, partnerName } = membership;

  return (
    <OAuthModal
      isVisible={errorCode !== null && !isRoomWelcomeOpen}
      title={
        errorCode === "MEMBERSHIP_INACTIVE"
          ? t("Access rejected")
          : t("Connect your account")
      }
      text={
        errorCode === "MEMBERSHIP_INACTIVE"
          ? t(
              "Your access to {{roomName}} room has not been approved. Please login to your {{roomName}} account on a desktop to fix any issue with your subscription.",
              { roomName: partnerName }
            )
          : t(
              "Access to this space is limited to active {{roomName}} members with an up-to-date paid subscription. Please verify your membership status to continue.",
              { roomName: partnerName }
            )
      }
      connectText={
        errorCode === "MEMBERSHIP_INACTIVE"
          ? t("Renew my subscription")
          : t("Login with {{roomName}} credentials", {
              roomName: partnerName,
            })
      }
      laterText={t("Cancel")}
      roomName={getRoomName(roomQuery.body, roomTextLang)}
      roomLogoUrl={getRoomLogoUrl(roomQuery.body)}
      onConnect={() => {
        void membership.connect();
      }}
      onLater={membership.later}
    />
  );
}
